import {
  BooleanLimit,
  CustomProductAttributeConfig,
  ListLimit,
  RangeLimit,
} from '../types';
import { UntypedFormGroup, Validators } from '@angular/forms';
import cloneDeep from 'lodash/cloneDeep';

function findAttributeUsages(
  attributeId,
  wholeConfig: Array<CustomProductAttributeConfig>
) {
  return wholeConfig.reduce((prev, element) => {
    switch (element.type) {
      case 'boolean':
        const usedInDeliveryTimeExpandedModification = element.booleanConfig
          .deliveryTimeModifier
          ? (checkConfig(
              attributeId,
              element.booleanConfig.deliveryTimeExpandedModification
            ) as Array<any>)
          : [];
        const usedInPriceExpandedModification = element.booleanConfig
          .priceModifier
          ? (checkConfig(
              attributeId,
              element.booleanConfig.priceExpandedModification
            ) as Array<any>)
          : [];
        const usedInCheckLimitations = checkLimitations(
          attributeId,
          wholeConfig
        ) as Array<any>;

        return [
          ...prev,
          ...usedInDeliveryTimeExpandedModification,
          ...usedInPriceExpandedModification,
          ...usedInCheckLimitations,
        ];
      case 'list':
      case 'decoration':
      case 'size':
        const configFromOptions = element.options
          .filter(
            ({
              hasExpandedDeliveryTimeModification,
              hasExpandedPrice,
              priceModifier,
              deliveryTimeModifier,
            }) =>
              (deliveryTimeModifier && hasExpandedDeliveryTimeModification) ||
              (priceModifier && hasExpandedPrice)
          )
          .reduce(
            (previousValue, currentValue) =>
              previousValue.concat(
                currentValue.deliveryTimeExpandedModification.filter(
                  () =>
                    currentValue.deliveryTimeModifier &&
                    currentValue.hasExpandedDeliveryTimeModification
                ),
                currentValue.priceExpandedModification.filter(
                  () =>
                    currentValue.priceModifier && currentValue.hasExpandedPrice
                )
              ),
            []
          );
        const limitationsFromOptions = element.options
          .filter(({ limitations }) => limitations.length)
          .reduce(
            (previousValue, currentValue) =>
              previousValue.concat(
                currentValue.limitations.reduce(
                  (previousValue1, currentValue1) =>
                    previousValue1.concat(Object.keys(currentValue1)),
                  []
                )
              ),
            []
          );
        return [
          ...prev,
          ...checkConfig(attributeId, configFromOptions),
          ...limitationsFromOptions.filter((x) => x === attributeId),
          ...checkLimitations(attributeId, wholeConfig),
        ];
      case 'counter':
        return [
          ...prev,
          ...(element.counterConfig.deliveryTimeModifier
            ? checkConfig(
                attributeId,
                element.counterConfig.deliveryTimeExpandedModification
              )
            : []),
          ...(element.counterConfig.priceModifier
            ? checkConfig(
                attributeId,
                element.counterConfig.priceExpandedModification
              )
            : []),
          ...checkLimitations(attributeId, wholeConfig),
        ];
    }
    return [
      ...prev,
      ...checkConfig(
        attributeId,
        element.booleanConfig.deliveryTimeExpandedModification
      ),
      ...checkConfig(
        attributeId,
        element.booleanConfig.priceExpandedModification
      ),
      ...checkLimitations(attributeId, wholeConfig),
    ];
  }, []);
}

function findOptionUsages(
  attributeId,
  wholeConfig: Array<CustomProductAttributeConfig>
) {
  return wholeConfig.reduce((prev, element) => {
    switch (element.type) {
      case 'boolean':
        return [
          ...prev,
          ...checkConfigForOption(
            attributeId,
            element.booleanConfig.deliveryTimeExpandedModification
          ),
          ...checkConfigForOption(
            attributeId,
            element.booleanConfig.priceExpandedModification
          ),
          ...checkLimitationsForOption(attributeId, wholeConfig),
        ];
      case 'list':
      case 'size':
        const configFromOptions = element.options
          .filter(
            ({
              hasExpandedDeliveryTimeModification,
              hasExpandedPrice,
              priceModifier,
              deliveryTimeModifier,
            }) =>
              (deliveryTimeModifier && hasExpandedDeliveryTimeModification) ||
              (priceModifier && hasExpandedPrice)
          )
          .reduce(
            (previousValue, currentValue) =>
              previousValue.concat(
                currentValue.deliveryTimeExpandedModification
                  .filter(
                    () =>
                      currentValue.hasExpandedDeliveryTimeModification &&
                      currentValue.deliveryTimeModifier
                  )
                  .map(({ config }) => config),
                currentValue.priceExpandedModification
                  .filter(
                    () =>
                      currentValue.hasExpandedPrice &&
                      currentValue.priceModifier
                  )
                  .map(({ config }) => config)
              ),
            []
          );
        return [
          ...prev,
          ...checkConfigForOption(attributeId, configFromOptions),
        ];
      case 'counter':
        return [
          ...checkConfigForOption(
            attributeId,
            element.counterConfig.deliveryTimeExpandedModification
          ),
          ...checkConfigForOption(
            attributeId,
            element.counterConfig.priceExpandedModification
          ),
          ...checkLimitationsForOption(attributeId, wholeConfig),
        ];
    }

    return [
      ...checkConfigForOption(
        attributeId,
        element.booleanConfig.deliveryTimeExpandedModification
      ),
      ...checkConfigForOption(
        attributeId,
        element.booleanConfig.priceExpandedModification
      ),
      ...checkLimitationsForOption(attributeId, wholeConfig),
    ];
  }, []);
}

export function findDependencies(
  attributeIndex,
  attributeId,
  wholeConfig: Array<CustomProductAttributeConfig>
) {
  let found = 0;
  const elementsAbove = wholeConfig
    .filter(
      (config, index) =>
        config.customId !== attributeId && index < attributeIndex
    )
    .map((value) => value.customId);

  const currentElement = wholeConfig.find(
    ({ customId }) => customId === attributeId
  );
  found += currentElement.limitations.reduce(
    (prev, limitation) =>
      prev +
      elementsAbove.filter((id) => Object.keys(limitation).indexOf(id) !== -1)
        .length,
    0
  );

  switch (currentElement.type) {
    case 'boolean':
      if (
        currentElement.booleanConfig.priceModifier &&
        currentElement.booleanConfig.hasExpandedPrice
      ) {
        found += currentElement.booleanConfig.priceExpandedModification.reduce(
          (prev, { config }) =>
            prev +
            elementsAbove.filter((id) => Object.keys(config).indexOf(id) !== -1)
              .length,
          0
        );
      }
      if (
        currentElement.booleanConfig.deliveryTimeModifier &&
        currentElement.booleanConfig.hasExpandedDeliveryTimeModification
      ) {
        found +=
          currentElement.booleanConfig.deliveryTimeExpandedModification.reduce(
            (prev, { config }) =>
              prev +
              elementsAbove.filter(
                (id) => Object.keys(config).indexOf(id) !== -1
              ).length,
            0
          );
      }
      break;
    case 'counter':
      if (
        currentElement.counterConfig.priceModifier &&
        currentElement.counterConfig.hasExpandedPrice
      ) {
        found += currentElement.counterConfig.priceExpandedModification.reduce(
          (prev, { config }) =>
            prev +
            elementsAbove.filter((id) => Object.keys(config).indexOf(id) !== -1)
              .length,
          0
        );
      }
      if (
        currentElement.counterConfig.deliveryTimeModifier &&
        currentElement.counterConfig.hasExpandedDeliveryTimeModification
      ) {
        found +=
          currentElement.counterConfig.deliveryTimeExpandedModification.reduce(
            (prev, { config }) =>
              prev +
              elementsAbove.filter(
                (id) => Object.keys(config).indexOf(id) !== -1
              ).length,
            0
          );
      }
      break;

    case 'list':
    case 'size':
      currentElement.options.forEach((option) => {
        found += option.limitations.filter((limitation) =>
          elementsAbove.find((id) => Object.keys(limitation).indexOf(id) !== -1)
        ).length;
        if (option.priceModifier && option.hasExpandedPrice) {
          found += option?.priceExpandedModification?.filter(({ config }) =>
            elementsAbove.find((id) => Object.keys(config).indexOf(id) !== -1)
          ).length;
        }
        if (
          option.deliveryTimeModifier &&
          option.hasExpandedDeliveryTimeModification
        ) {
          found += option.deliveryTimeExpandedModification?.reduce(
            (prev, { config }) =>
              prev +
              elementsAbove.filter(
                (id) => Object.keys(config).indexOf(id) !== -1
              ).length,
            0
          );
        }
      });
      break;
  }
  return found;
}

export function findUsages(
  attributeIndex,
  attributeId,
  wholeConfig: Array<CustomProductAttributeConfig>,
  optionId = null
) {
  let found = false;
  const elementsBelow = wholeConfig.filter(
    (config, index) => config.customId !== attributeId && index > attributeIndex
  );
  let res = [];
  if (optionId === null) {
    res = findAttributeUsages(attributeId, elementsBelow);
  } else {
    res = findOptionUsages(optionId, elementsBelow);
  }
  return new Set(res);
}

function checkLimitations(
  attributeId,
  wholeConfig: Array<CustomProductAttributeConfig>
) {
  const filtered = wholeConfig.filter(({ limitations }) => {
    console.log(Object.keys(limitations));
    return (
      limitations.filter(
        (param) =>
          Object.keys(param).filter((param) => param === attributeId).length > 0
      ).length > 0
    );
  });
  return filtered;
}

function checkConfig(
  attributeId: string,
  config: Array<{ config: { [x: string]: any } }>
) {
  if (typeof config === 'undefined') {
    return [];
  }

  return config.filter(
    ({ config }) => Object.keys(config).indexOf(attributeId) !== -1
  );
}

function checkLimitationsForOption(
  attributeId,
  wholeConfig: Array<CustomProductAttributeConfig>
) {
  return wholeConfig.filter(
    ({ limitations }) =>
      limitations.filter(
        (value) =>
          Object.values(value).filter(
            (param) =>
              Array.isArray((param as ListLimit).value) &&
              (param as ListLimit).value.indexOf(attributeId) !== -1
          ).length > 0
      ).length > 0
  );
}

function checkConfigForOption(
  attributeId: string,
  config: Array<{ config: { [x: string]: any } }>
) {
  if (typeof config === 'undefined') {
    return [];
  }
  return config.filter(
    ({ config }) => Object.values(config).indexOf(attributeId) !== -1
  );
}

export function unFormatNumber(price) {
  return Number(
    String(price)
      .replace(',', '.')
      .replace(/[^\d.-]/g, '')
  );
}

export function orderConfig(config: { [x: string]: any }) {
  const sorted = [];
  config = cloneDeep(config);
  Object.entries(config)
    .sort(([a], [b]) => {
      if (a > b) {
        return -1;
      }
      if (b > a) {
        return 1;
      }
      return 0;
    })
    .forEach((v) => {
      if (Array.isArray(v[1]?.value)) {
        // delete v[1].value;
      }
      if (typeof v[1]?.min !== 'undefined') {
        delete v[1].min;
      }
      if (typeof v[1]?.max !== 'undefined') {
        delete v[1].max;
      }
      sorted.push(v);
    });
  return sorted;
}

export function updateAttributeValidators(current, model: UntypedFormGroup) {
  model.controls.deliveryTimeExpandedModification.clearValidators();
  model.controls.simpleDeliveryTimeModification.clearValidators();
  model.controls.priceModifier.clearValidators();
  model.controls.priceSimpleModification.clearValidators();
  if (current.deliveryTimeModifier) {
    if (current.hasExpandedDeliveryTimeModification) {
      model.controls.deliveryTimeExpandedModification.setValidators(
        Validators.required
      );
    } else {
      model.controls.simpleDeliveryTimeModification.setValidators(
        Validators.required
      );
    }
  }
  if (current.priceModifier) {
    if (current.hasExpandedPrice) {
      model.controls.priceExpandedModification.setValidators(
        Validators.required
      );
    } else {
      model.controls.priceSimpleModification.setValidators(Validators.required);
    }
  }
}

export function configToObject(current) {
  const config = {};
  Object.entries(current)
    .filter(
      ([index, value]) =>
        typeof (value as RangeLimit)?.min !== 'undefined' ||
        !!(value as BooleanLimit)?.use ||
        (Array.isArray((value as ListLimit)?.value) &&
          (value as ListLimit).value.length)
    )
    .forEach(([index, value]) => (config[index] = value));
  return config;
}
